/*
 * /api/brand (milestone 6): whose brand to show. Without an org it's the
 * brand of the host the panel is served on (a custom panel domain, or
 * Hosterr's); with one, it's that org's agency, so an agency's clients only
 * ever see the agency.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { applyBrand, HOSTERR_BRAND, type Brand } from "../lib/brand";
import { api, isApiError, qs } from "./client";

export const brandKey = (orgId: string | null) => ["brand", orgId ?? "host"] as const;

/** Fetch and apply the brand. Failing is fine: the panel keeps the brand it has. */
export function useApplyBrand(orgId: string | null) {
  const q = useQuery({
    queryKey: brandKey(orgId),
    queryFn: ({ signal }) => api.get<{ brand: Partial<Brand>; source?: string }>(`/brand${qs({ orgId })}`, signal),
    select: (d) => ({ ...HOSTERR_BRAND, ...d.brand }) as Brand,
    staleTime: 5 * 60_000,
    retry: false,
  });
  useEffect(() => {
    if (q.data) applyBrand(q.data);
    // No brand endpoint (an API from before milestone 6): Hosterr's own, rather than whatever was cached.
    else if (isApiError(q.error) && q.error.status === 404) applyBrand(HOSTERR_BRAND);
  }, [q.data, q.error]);
  return q;
}
